"use client";

// The age gate and the sign-up agreement in front of the popup's existing sign-up funnel (youth spec v1.1, founder 2026-10-20 21:14 + 21:29).
// Step one is the birth month and year, nothing pre-filled and no hint of which ages are accepted. Under 13 sees "Ask a parent to add you":
// nothing is sent, stored or measured, and the answers are discarded. 13 and over continue to the same funnel, under the ONE required unticked
// box carrying the served agreement label (GET /api/auth/signup-agreement) and its three public documents (new tab, so nothing typed is lost).
// The funnel's submit is wrapped: it adds birthMonth / birthYear, agreementAccepted: true and the three served version ids, and nothing is
// sent while the box is unticked or the agreement could not be loaded. A 409 (documents changed) reloads them and unticks the box; a 403 (the
// server's own age check) shows the parent screen and drops every answer.
import { useEffect, useId, useState } from "react";
import type { ReactNode } from "react";

import type { SignupAgreement } from "@repo/ui/lib/signup-age";
import { syncAdPixels } from "@/lib/ad-pixels";
import { enterEssentialOnlyMode } from "@/lib/essential-only";
import { accountsUrl } from "@/lib/account-links";
import { AGREE_REQUIRED, agreementSegments, ASK_A_PARENT, BIRTH_MONTHS, BIRTH_REQUIRED, birthYears, parseSignupAgreement, signupAgeBand } from "@repo/ui/lib/signup-age";

export interface GateFields {
  birthMonth: number;
  birthYear: number;
  agreementAccepted: true;
  termsVersion: string;
  privacyVersion: string;
  membershipVersion: string;
}
interface Result {
  ok: boolean;
  error?: string;
  status?: number;
}
/** The public SnowClub document behind each name: on the approved Accounts origin, where /terms, /privacy and /membership are public (Root's
 *  own /terms is the old SnowBus page). Opened in a new tab so nothing typed here is lost. */
export const signupDocHref = (slug: string) => accountsUrl(`/${slug}`);
/** The served agreement (same-origin, never cached), or null when it is unavailable or not the documented shape. */
async function loadSignupAgreement(): Promise<SignupAgreement | null> {
  try {
    const res = await fetch("/api/auth/signup-agreement", { cache: "no-store" });
    return res.ok ? parseSignupAgreement(await res.json()) : null;
  } catch {
    return null;
  }
}
const UNAVAILABLE = "We can't load the sign-up agreement right now, so an account can't be created. Please try again.";

export function SignupGate<F>({
  orange,
  grey,
  onCancel,
  signUp,
  children,
}: {
  orange: string;
  grey: string;
  onCancel: () => void;
  signUp: (fields: F & GateFields) => Promise<Result>;
  children: (onSubmit: (fields: F) => Promise<Result>) => ReactNode;
}) {
  const id = useId();
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [age, setAge] = useState<{ birthMonth: number; birthYear: number } | null>(null);
  const [under13, setUnder13] = useState(false);
  const [ageError, setAgeError] = useState<string | null>(null);
  const [years] = useState(() => birthYears(Date.now())); // fixed for the life of the step
  const [agreement, setAgreement] = useState<SignupAgreement | null | undefined>(undefined);
  const [agreed, setAgreed] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  // Retry and the 409 path re-read the agreement from their handlers; the first read lands from the promise (undefined = loading).
  const reload = async () => setAgreement(await loadSignupAgreement());
  useEffect(() => {
    let live = true;
    void loadSignupAgreement().then((served) => {
      if (live) setAgreement(served);
    });
    return () => {
      live = false;
    };
  }, []);

  const discard = () => {
    setMonth("");
    setYear("");
    setAge(null);
    setAgreed(false);
  };

  if (under13) {
    return (
      <div role="status" data-signup-under13 style={{ textAlign: "center", padding: "24px 8px" }}>
        <h3 style={{ margin: "0 0 8px", fontSize: "18px" }}>{ASK_A_PARENT}</h3>
        <p style={{ margin: "0 0 16px", color: grey, fontSize: "13px" }}>Young skiers join SnowClub through a parent or guardian, who adds them from their own account.</p>
        <button type="button" onClick={() => { setUnder13(false); onCancel(); }} style={{ background: "none", border: 0, color: orange, cursor: "pointer", font: "inherit" }}>Back</button>
      </div>
    );
  }

  if (!age) {
    const next = (e: React.FormEvent) => {
      e.preventDefault();
      const band = month && year ? signupAgeBand(Number(month), Number(year), Date.now(), agreement?.minimumAge) : null;
      if (band === null) return setAgeError(BIRTH_REQUIRED);
      // An answer under 16 switches this page to essential-only measurement, in memory only (nothing written about the visitor).
      if (band !== "member") {
        enterEssentialOnlyMode();
        syncAdPixels();
      }
      if (band === "under13") {
        discard();
        setUnder13(true);
        return;
      }
      setAge({ birthMonth: Number(month), birthYear: Number(year) });
    };
    const select = { flex: "1 1 120px", minHeight: "40px", padding: "0 10px", border: "1px solid #ccc", borderRadius: "6px", background: "#fff", font: "inherit" } as const;
    return (
      <form onSubmit={next} noValidate data-signup-age style={{ paddingTop: "20px" }}>
        <fieldset style={{ border: 0, margin: 0, padding: 0, display: "flex", flexWrap: "wrap", gap: "8px" }} aria-describedby={ageError ? `${id}-err` : undefined}>
          <legend style={{ fontWeight: 700, marginBottom: "8px" }}>First, when were you born?</legend>
          <select aria-label="Birth month" value={month} onChange={(e) => { setMonth(e.target.value); setAgeError(null); }} style={select} data-signup-month>
            <option value="">Month</option>
            {BIRTH_MONTHS.map((label, i) => <option key={label} value={i + 1}>{label}</option>)}
          </select>
          <select aria-label="Birth year" value={year} onChange={(e) => { setYear(e.target.value); setAgeError(null); }} style={select} data-signup-year>
            <option value="">Year</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </fieldset>
        {ageError ? <p id={`${id}-err`} role="alert" style={{ color: "#b42318", fontSize: "13px" }}>{ageError}</p> : null}
        <button type="submit" data-signup-age-next style={{ marginTop: "14px", width: "100%", minHeight: "44px", border: 0, borderRadius: "6px", background: orange, color: "#fff", fontWeight: 700, cursor: "pointer" }}>Continue</button>
        <button type="button" onClick={onCancel} style={{ marginTop: "8px", width: "100%", background: "none", border: 0, color: grey, cursor: "pointer", font: "inherit" }}>Cancel</button>
      </form>
    );
  }

  const submit = async (fields: F): Promise<Result> => {
    if (!agreement) return { ok: false, error: UNAVAILABLE };
    if (!agreed) return { ok: false, error: AGREE_REQUIRED };
    const r = await signUp({ ...fields, ...age, agreementAccepted: true, ...agreement.versions });
    if (r.status === 409) {
      setAgreed(false);
      setNote("The agreement was updated. Review it and tick the box again.");
      await reload();
    } else if (r.status === 403) {
      discard();
      setUnder13(true);
    }
    return r;
  };

  return (
    <>
      <div data-signup-agreement style={{ display: "flex", gap: "8px", alignItems: "flex-start", margin: "16px 0 4px", fontSize: "13px" }}>
        <input id={`${id}-agree`} type="checkbox" checked={agreed} onChange={(e) => { setAgreed(e.target.checked); setNote(null); }} aria-required="true" style={{ marginTop: "3px", accentColor: orange }} disabled={!agreement} />
        <label htmlFor={`${id}-agree`}>
          {agreement
            ? agreementSegments(agreement.agreementLabel).map((seg, i) =>
                "slug" in seg ? <a key={i} href={signupDocHref(seg.slug)} target="_blank" rel="noopener noreferrer" style={{ color: orange }}>{seg.text}</a> : <span key={i}>{seg.text}</span>,
              )
            : agreement === null ? UNAVAILABLE : "Loading the agreement…"}
        </label>
      </div>
      {agreement === null ? <button type="button" onClick={() => void reload()} style={{ background: "none", border: 0, color: orange, cursor: "pointer", font: "inherit", fontSize: "13px" }}>Try again</button> : null}
      {note ? <p role="alert" style={{ color: "#b42318", fontSize: "13px", margin: "4px 0" }}>{note}</p> : null}
      {children(submit)}
    </>
  );
}
